import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Button } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetAccountingDocumentsDocument,
  type AccountingDocument,
  type GetAccountingDocumentsQuery,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import {
  accountingDocumentStateColors,
  translateAccountingDocumentState,
} from '@/utils/billingStateLabels';
import { Download } from '@mui/icons-material';

interface DocumentRow {
  id: string;
  tenantId: string;
  customerName: string;
  organizationId?: string | null;
  number: string;
  type: string;
  state: string;
  amountNet: number;
  amountGross: number;
  taxAmount: number;
  currencyCode: string;
  createdAt: string;
  sentAt?: string | null;
  subscription?: { number: string } | null;
  [key: string]: unknown;
}

export default function Invoices() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const typeLabels: Record<string, string> = {
    INVOICE: t('invoices.docTypeInvoice'),
    CREDIT_NOTE: t('invoices.docTypeCreditNote'),
    DEBIT_NOTE: t('invoices.docTypeDebitNote'),
    RECEIPT: t('invoices.docTypeReceipt'),
  };

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    amount != null
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
          amount,
        )
      : '–';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading } = useQuery<GetAccountingDocumentsQuery>(GetAccountingDocumentsDocument, {
    variables: { first: 50 },
  });

  const documents = data?.accountingDocuments?.nodes ?? [];
  const totalCount = data?.accountingDocuments?.totalCount ?? 0;

  const handleDownloadPdf = (accountingDocument: AccountingDocument) => {
    if (!accountingDocument.pdfDataBase64 || accountingDocument.pdfDataBase64.trim() === '') {
      alert(t('common.NoFileAvailableForDownload'));
      return;
    }

    const formattedDate = formatDate(accountingDocument.createdAt) || '';
    const filename = `${accountingDocument.number}_${formattedDate.replace(/[^0-9]/g, '')}.pdf`;
    const downloadLink = document.createElement('a');
    downloadLink.href = `data:application/pdf;base64,${accountingDocument.pdfDataBase64}`;
    downloadLink.download = filename;
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href);
  };
  const rows: DocumentRow[] = documents.map((d) => ({
    id: d.id,
    tenantId: d.tenantId,
    customerName: d.subscription?.organizationUnit?.organization?.name ?? d.tenant?.name ?? '–',
    organizationId: d.subscription?.organizationUnit?.organization?.id ?? null,
    number: d.number,
    type: d.type,
    state: d.state,
    amountNet: d.amountNet,
    amountGross: d.amountGross,
    taxAmount: d.taxAmount,
    currencyCode: d.currencyCode,
    createdAt: d.createdAt,
    sentAt: d.sentAt,
    subscription: d.subscription,
    pdfDataBase64: d.pdfDataBase64,
  }));

  const columns: GridColDef[] = [
    {
      field: 'customerName',
      headerName: t('customers.customerOrg'),
      width: 200,
      valueGetter: (_v, row) => row.customerName,
      flex: 1,
    },
    { field: 'number', headerName: t('common.number'), width: 140, flex: 1 },
    {
      field: 'type',
      headerName: t('common.type'),
      width: 100,
      valueFormatter: (value: string) => typeLabels[value] || value,
      flex: 1,
    },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 100,
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={translateAccountingDocumentState(t, String(params.value))}
          size="small"
          color={accountingDocumentStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    {
      field: 'amountNet',
      headerName: t('common.net'),
      width: 100,
      valueFormatter: (value: number) => formatCurrency(value),
      flex: 1,
    },
    {
      field: 'amountGross',
      headerName: t('common.gross'),
      width: 100,
      valueFormatter: (value: number) => formatCurrency(value),
      flex: 1,
    },
    {
      field: 'taxAmount',
      headerName: t('common.vat'),
      width: 90,
      valueFormatter: (value: number) => formatCurrency(value),
      flex: 1,
    },
    {
      field: 'subscription',
      headerName: t('invoices.subscription'),
      width: 120,
      valueGetter: (value: unknown) => (value as { number?: string })?.number ?? '–',
      flex: 1,
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
      flex: 1,
    },
    {
      field: 'sentAt',
      headerName: t('common.sent'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
      flex: 1,
    },
    {
      field: 'pdfDataBase64',
      headerName: t('invoices.pdf'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      width: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="error"
          size="small"
          endIcon={<Download />}
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadPdf(params.row);
          }}
        >
          {t('PDF')}
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 180px)', width: '100%', minWidth: 0 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('invoices.title')}
      </Typography>
      <DataTable<DocumentRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        paginationMode="client"
      />
    </Box>
  );
}
